import { clamp, hexToRgb, luminance, mixRgb, type Rgb } from '../../engine/math';
import { keyBlendOf } from '../../engine/keyedBackdrop';
import type { ChromeTone } from '../../engine/types';

// Three key lights in the glasshouse: a cool, misty morning, bright midday,
// and the warm golden afternoon of the finished garden.
const STOPS = {
  skyTop: ['#BFD9E6', '#CFE6F2', '#F2D9B0'],
  skyLow: ['#E4EEE6', '#F3F6EC', '#FBE3B8'],
  outside: ['#A9C4B4', '#9DC4A4', '#C9B384'],
  outsideFar: ['#C8DAD2', '#C4DCC8', '#E3CDA2'],
  glass: ['#DCEBEF', '#E8F3F4', '#FBEBCF'],
  frame: ['#F4F5F0', '#FFFFFF', '#FFF4E0'],
  frameShade: ['#B7C4C4', '#C2CCC6', '#D6BE98'],
  brick: ['#B06E58', '#BC7458', '#C27A50'],
  brickShade: ['#7E4A3E', '#87503E', '#8A4E36'],
  floor: ['#D9CDB8', '#E4D6BC', '#EBCF9F'],
  floorShade: ['#B3A58E', '#BFAE90', '#C7A576'],
  tile: ['#C98A6C', '#D3906E', '#D98E5E'],
  soil: ['#5E4636', '#654A38', '#6A4A30'],
  soilLit: ['#80624C', '#8A6A50', '#9A7048'],
  wood: ['#8E6A4E', '#9A7352', '#A9764A'],
  woodShade: ['#5E4434', '#664836', '#6E4630'],
  pot: ['#C46E48', '#CF7448', '#D87A42'],
  potShade: ['#8C4A32', '#944C30', '#9A4A2A'],
  leafDark: ['#2F5A44', '#2E6244', '#3A5A30'],
  leaf: ['#4D8A5C', '#4E9A58', '#6A9A44'],
  leafLit: ['#8FC27E', '#9AD07A', '#C6CF6A'],
  shaft: ['#F4FBFF', '#FFFDF0', '#FFE2A8'],
  haze: ['#E8F2F2', '#F6F8EE', '#FFE6BC'],
} as const;

type ColorName = keyof typeof STOPS;
export type GreenhouseColors = Record<ColorName, Rgb>;

export const GREENHOUSE_KEYS = 3;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = {} as Record<ColorName, Rgb[]>;
NAMES.forEach((name) => {
  PARSED[name] = STOPS[name].map(hexToRgb);
});

export const greenhouseKeyColors = (index: number): GreenhouseColors => {
  const key = Math.min(GREENHOUSE_KEYS - 1, Math.max(0, Math.round(index) || 0));
  const colors = {} as GreenhouseColors;
  NAMES.forEach((name) => {
    colors[name] = PARSED[name][key];
  });
  return colors;
};

/** The day in the glasshouse: morning to midday with the questions, golden afternoon at the finish. */
export const greenhouseLight = (progress: number, finale: number) =>
  clamp(clamp(progress) * 0.5 + clamp(finale) * 0.5);

export const greenhouseColors = (light: number): GreenhouseColors => {
  const { index, amount } = keyBlendOf(light, GREENHOUSE_KEYS);
  const colors = {} as GreenhouseColors;
  NAMES.forEach((name) => {
    colors[name] = mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  });
  return colors;
};

/** Bright glass overhead all day, so the header keeps its dark text. */
export const greenhouseChromeTone = (progress: number): ChromeTone =>
  luminance(greenhouseColors(greenhouseLight(progress, progress >= 0.995 ? 1 : 0)).skyTop) > 0.3 ? 'dark' : 'light';
